import React, { useState } from 'react';
import { useMetadataStore } from '../hooks/useMetadataStore';
import { SectionList } from '../components/sections/SectionList';
import { FieldMappingPanel } from '../components/fieldMappings/FieldMappingPanel';
import { PicklistOptionList } from '../components/picklists/PicklistOptionList';
import { Layers, List, Database, Settings, Play } from 'lucide-react';
import { FormPreview } from '../components/preview/FormPreview';
export function AdminDashboard() {
  const store = useMetadataStore();
  const [activeTab, setActiveTab] = useState<'sections' | 'fields' | 'picklists'>('sections');
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const stats = [{
    label: 'Total Sections',
    value: store.sections.length,
    icon: Layers,
    color: 'text-blue-600',
    bg: 'bg-blue-50'
  }, {
    label: 'Field Mappings',
    value: store.fieldMappings.length,
    icon: Database,
    color: 'text-purple-600',
    bg: 'bg-purple-50'
  }, {
    label: 'Picklist Options',
    value: store.picklistOptions.length,
    icon: List,
    color: 'text-emerald-600',
    bg: 'bg-emerald-50'
  }];
  const tabs = [{
    key: 'sections' as const,
    label: 'Sections',
    icon: Layers
  }, {
    key: 'fields' as const,
    label: 'Field Mappings',
    icon: Database
  }, {
    key: 'picklists' as const,
    label: 'Picklist Options',
    icon: List
  }];
  return <div className="max-w-[1600px] mx-auto p-4 sm:p-6 lg:p-8" data-id="element-466">
      {/* Header */}
      <div className="mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-467">
        <div className="flex items-center gap-3" data-id="element-468">
          <div className="p-2.5 bg-blue-600 rounded-xl" data-id="element-469">
            <Settings className="w-5 h-5 text-white" data-id="element-470" />
          </div>
          <div data-id="element-471">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 leading-tight" data-id="element-472">
              AL 1003 Form Configuration
            </h1>
            <p className="text-gray-500 text-sm mt-0.5" data-id="element-473">
              Manage form sections, field mappings, and picklist options for the
              dynamic mortgage application capture system.
            </p>
          </div>
        </div>
        <button onClick={() => setIsPreviewOpen(true)} className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors" data-id="element-474">
          <Play className="w-4 h-4 mr-2 text-blue-600" data-id="element-475" />
          Live Preview
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6" data-id="element-476">
        {stats.map((stat, i) => <div key={i} className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm flex items-center gap-4" data-id="element-477">
            <div className={`p-2.5 rounded-xl ${stat.bg}`} data-id="element-478">
              <stat.icon className={`w-5 h-5 ${stat.color}`} data-id="element-479" />
            </div>
            <div data-id="element-480">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide" data-id="element-481">
                {stat.label}
              </p>
              <p className="text-2xl font-bold text-gray-900 tabular-nums" data-id="element-482">
                {stat.value}
              </p>
            </div>
          </div>)}
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col min-h-[600px]" data-id="element-483">
        <div className="border-b border-gray-200 px-4 sm:px-6 flex-shrink-0" data-id="element-484">
          <nav className="-mb-px flex space-x-6" aria-label="Tabs" data-id="element-485">
            {tabs.map(tab => <button key={tab.key} onClick={() => setActiveTab(tab.key)} className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors flex items-center gap-2 ${activeTab === tab.key ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-486">
                <tab.icon className="w-4 h-4" data-id="element-487" />
                {tab.label}
              </button>)}
          </nav>
        </div>

        <div className="p-4 sm:p-6 flex-1 bg-slate-50/60" data-id="element-488">
          {activeTab === 'sections' && <SectionList sections={store.sections} getFieldCount={store.getFieldCountForSection} onAdd={store.addSection} onUpdate={store.updateSection} onDelete={store.deleteSection} onReorder={store.reorderSection} data-id="element-489" />}

          {activeTab === 'fields' && <FieldMappingPanel sections={store.sections} fields={store.fieldMappings} getFieldCount={store.getFieldCountForSection} getOptionCount={store.getOptionCountForField} onAdd={store.addFieldMapping} onUpdate={store.updateFieldMapping} onDelete={store.deleteFieldMapping} onReorder={store.reorderFieldMapping} data-id="element-490" />}

          {activeTab === 'picklists' && <PicklistOptionList options={store.picklistOptions} fields={store.fieldMappings} onAdd={store.addPicklistOption} onBulkAdd={store.addBulkPicklistOptions} onUpdate={store.updatePicklistOption} onDelete={store.deletePicklistOption} onReorder={store.reorderPicklistOption} data-id="element-491" />}
        </div>
      </div>

      <FormPreview isOpen={isPreviewOpen} onClose={() => setIsPreviewOpen(false)} sections={store.sections} fields={store.fieldMappings} picklistOptions={store.picklistOptions} data-id="element-492" />
    </div>;
}